/**
 * Local SQLite adapters for projects.
 * Mirrors the Convex api.projects.* interface.
 */
import { localDb, allCraftspacesDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocalProject {
  _id: string;
  public_id?: string;
  craftspace_id: string;
  name: string;
  description?: string;
  thumbnail_url?: string;
  data?: Record<string, unknown>;
  order?: number;
  created_at?: string;
  updated_at?: string;
  // First panorama thumbnail for dashboard preview (joined)
  first_panorama_thumbnail?: string;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useProjectsByCraftspace(craftspaceId: string | undefined): LocalProject[] | undefined {
  return useLocalQuery<LocalProject>(
    `SELECT p.id as _id, p.*,
       (SELECT pano.thumbnail_path FROM panoramas pano
        WHERE pano.project_id = p.id ORDER BY pano.order_index ASC LIMIT 1) as first_panorama_thumbnail
     FROM projects p
     WHERE p.craftspace_id = ?
     ORDER BY p."order" ASC, p.name ASC`,
    [craftspaceId ?? ""],
    ["projects", "panoramas"],
    ["data"],
    craftspaceId, // craftspace DB
  );
}

/** Returns all projects across all craftspace DBs (for dashboard aggregate view). */
export function useProjects(): LocalProject[] | undefined {
  return useLocalQuery<LocalProject>(
    `SELECT p.id as _id, p.*,
       (SELECT pano.thumbnail_path FROM panoramas pano
        WHERE pano.project_id = p.id ORDER BY pano.order_index ASC LIMIT 1) as first_panorama_thumbnail
     FROM projects p
     ORDER BY p."order" ASC, p.name ASC`,
    [],
    ["projects", "panoramas"],
    ["data"],
    "all", // aggregate all craftspace DBs
  );
}

/** Look up a single project by id or public_id across all craftspace DBs. */
export function useProject(id: string | undefined): LocalProject | null | undefined {
  return useLocalGet<LocalProject>(
    "SELECT id as _id, * FROM projects WHERE id = ? OR public_id = ?",
    [id ?? "", id ?? ""],
    ["projects"],
    ["data"],
    "all", // search across all craftspace DBs
  );
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createProject(params: {
  name: string;
  craftspace_id: string;
  description?: string;
  data?: Record<string, unknown>;
}): Promise<string> {
  const id = newId();
  const publicId = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  const ts = now();
  await localDb(params.craftspace_id).run(
    `INSERT INTO projects (id, public_id, craftspace_id, name, description, data, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      publicId,
      params.craftspace_id,
      params.name,
      params.description ?? null,
      params.data ? JSON.stringify(params.data) : null,
      ts,
      ts,
    ],
  );
  invalidate("projects");
  return id;
}

export async function updateProject(params: {
  id: string;
  craftspace_id: string;
  name?: string;
  description?: string;
  thumbnail_url?: string;
  data?: Record<string, unknown>;
}): Promise<void> {
  const { id, craftspace_id, ...fields } = params;
  const setClauses: string[] = ["updated_at = ?"];
  const values: unknown[] = [now()];

  if (fields.name !== undefined) { setClauses.push("name = ?"); values.push(fields.name); }
  if (fields.description !== undefined) { setClauses.push("description = ?"); values.push(fields.description); }
  if (fields.thumbnail_url !== undefined) { setClauses.push("thumbnail_url = ?"); values.push(fields.thumbnail_url); }
  if (fields.data !== undefined) { setClauses.push("data = ?"); values.push(JSON.stringify(fields.data)); }

  values.push(id);
  await localDb(craftspace_id).run(`UPDATE projects SET ${setClauses.join(", ")} WHERE id = ?`, values);
  invalidate("projects");
}

export async function removeProject(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM projects WHERE id = ?", [id]);
  invalidate("projects", "panoramas", "hotspots");
}

export async function moveProjectToCraftspace(params: {
  id: string;
  sourceCraftspaceId: string;
  targetCraftspaceId: string;
}): Promise<void> {
  const { id, sourceCraftspaceId, targetCraftspaceId } = params;
  if (sourceCraftspaceId === targetCraftspaceId) return;

  const sourceDb = localDb(sourceCraftspaceId);
  const targetDb = localDb(targetCraftspaceId);

  const project = await sourceDb.get<Record<string, unknown>>(
    "SELECT * FROM projects WHERE id = ?",
    [id],
  );
  if (!project) {
    throw new Error(`Project ${id} not found in craftspace ${sourceCraftspaceId}`);
  }

  const quoteIdent = (name: string) => `"${name.replace(/"/g, "\"\"")}"`;
  const tableColumnsCache = new Map<string, string[]>();

  const getTableColumns = async (table: string): Promise<string[]> => {
    const cached = tableColumnsCache.get(table);
    if (cached) return cached;
    const info = await targetDb.query<{ name: string }>(`PRAGMA table_info(${quoteIdent(table)})`);
    const columns = info.map((row) => row.name);
    tableColumnsCache.set(table, columns);
    return columns;
  };

  const insertRows = async (table: string, rows: Record<string, unknown>[]) => {
    if (rows.length === 0) return;
    const targetColumns = await getTableColumns(table);
    const rowColumns = Object.keys(rows[0]);
    const columns = rowColumns.filter((col) => targetColumns.includes(col));
    if (columns.length === 0) return;

    const placeholders = columns.map(() => "?").join(", ");
    const sql = `INSERT OR REPLACE INTO ${table} (${columns.map(quoteIdent).join(", ")}) VALUES (${placeholders})`;
    for (const row of rows) {
      await targetDb.run(sql, columns.map((col) => row[col]));
    }
  };

  const projectRow = { ...project, craftspace_id: targetCraftspaceId };
  await insertRows("projects", [projectRow]);

  const panoramas = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM panoramas WHERE project_id = ?",
    [id],
  );
  await insertRows("panoramas", panoramas);

  const hotspots = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM hotspots WHERE project_id = ?",
    [id],
  );
  await insertRows("hotspots", hotspots);

  const cmsData = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM cms_data WHERE project_id = ?",
    [id],
  );
  await insertRows("cms_data", cmsData);

  const cmsRecords = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM cms_records WHERE project_id = ?",
    [id],
  );
  await insertRows("cms_records", cmsRecords);

  const publishedTours = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM published_tours WHERE project_id = ?",
    [id],
  );
  const publishedToursAdjusted = publishedTours.map((row) => ({
    ...row,
    craftspace_id: targetCraftspaceId,
  }));
  await insertRows("published_tours", publishedToursAdjusted);

  const variables = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM variables WHERE project_id = ?",
    [id],
  );
  await insertRows("variables", variables);

  const skins = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM skins WHERE project_id = ?",
    [id],
  );
  const skinsAdjusted = skins.map((row) => ({
    ...row,
    craftspace_id: targetCraftspaceId,
  }));
  await insertRows("skins", skinsAdjusted);

  const assetVersions = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM asset_versions WHERE project_id = ?",
    [id],
  );
  await insertRows("asset_versions", assetVersions);

  const snapshots = await sourceDb.query<Record<string, unknown>>(
    "SELECT * FROM project_snapshots WHERE project_id = ?",
    [id],
  );
  await insertRows("project_snapshots", snapshots);

  await sourceDb.run("DELETE FROM projects WHERE id = ?", [id]);

  invalidate(
    "projects",
    "panoramas",
    "hotspots",
    "cms_data",
    "cms_records",
    "published_tours",
    "variables",
    "skins",
    "asset_versions",
    "project_snapshots",
  );
}

export async function duplicateProject(id: string, craftspaceId: string, name?: string): Promise<string> {
  const source = await localDb(craftspaceId).get<{ name: string; craftspace_id: string; description: string; data: string }>(
    "SELECT * FROM projects WHERE id = ?",
    [id],
  );
  if (!source) throw new Error(`Project ${id} not found`);

  const newProjectId = await createProject({
    name: name ?? `${source.name} (copy)`,
    craftspace_id: source.craftspace_id,
    description: source.description,
    data: source.data ? JSON.parse(source.data) : undefined,
  });

  const ws = localDb(craftspaceId);

  // Duplicate panoramas
  const panoramas = await ws.query<{
    id: string; name: string; storage_path: string; thumbnail_path: string;
    width: number; height: number; order_index: number; metadata: string;
  }>(
    "SELECT * FROM panoramas WHERE project_id = ? ORDER BY order_index",
    [id],
  );

  for (const pano of panoramas) {
    const newPanoId = newId();
    const ts = now();
    await ws.run(
      `INSERT INTO panoramas (id, project_id, name, storage_path, thumbnail_path, width, height, order_index, metadata, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [newPanoId, newProjectId, pano.name, pano.storage_path, pano.thumbnail_path, pano.width, pano.height, pano.order_index, pano.metadata, ts, ts],
    );

    // Duplicate hotspots for this panorama
    const hotspots = await ws.query<Record<string, unknown>>(
      "SELECT * FROM hotspots WHERE panorama_id = ?",
      [pano.id],
    );
    for (const hs of hotspots) {
      const ts2 = now();
      await ws.run(
        `INSERT INTO hotspots (id, panorama_id, project_id, type, label,
           position_x, position_y, position_z,
           rotation_x, rotation_y, rotation_z,
           scale_x, scale_y, scale_z,
           visible, target_panorama_id, content, style, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          newId(),
          newPanoId,
          newProjectId,
          hs.type,
          hs.label,
          hs.position_x,
          hs.position_y,
          hs.position_z,
          (hs as Record<string, unknown>).rotation_x ?? 0,
          (hs as Record<string, unknown>).rotation_y ?? 0,
          (hs as Record<string, unknown>).rotation_z ?? 0,
          (hs as Record<string, unknown>).scale_x ?? 1,
          (hs as Record<string, unknown>).scale_y ?? 1,
          (hs as Record<string, unknown>).scale_z ?? 1,
          (hs as Record<string, unknown>).visible ?? 1,
          null,
          hs.content,
          hs.style,
          ts2,
          ts2,
        ],
      );
    }
  }

  invalidate("projects", "panoramas", "hotspots");
  return newProjectId;
}

export async function ensurePublicId(id: string, craftspaceId: string): Promise<string | null> {
  const row = await localDb(craftspaceId).get<{ public_id: string }>(
    "SELECT public_id FROM projects WHERE id = ?",
    [id],
  );
  if (row?.public_id) return row.public_id;

  const publicId = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  await localDb(craftspaceId).run("UPDATE projects SET public_id = ? WHERE id = ?", [publicId, id]);
  invalidate("projects");
  return publicId;
}

/** Resolve a project's craftspace_id from any craftspace DB. */
export async function getProjectCraftspaceId(projectId: string): Promise<string | null> {
  const rows = await allCraftspacesDb.query<{ craftspace_id: string }>(
    "SELECT craftspace_id FROM projects WHERE id = ? OR public_id = ?",
    [projectId, projectId],
  );
  return rows[0]?.craftspace_id ?? null;
}
